import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { cn } from "@/lib/utils";

import type { NarrationDriftReport } from "./narrationDrift";
import type { NarrationTimeline } from "./narrationTimeline";
import type { SyncTimeline } from "./syncTimeline";
import type { WaveformPeaks } from "./waveformPeaks";
import { peakAmplitude, resamplePeaks } from "./waveformPeaks";

// ---------------------------------------------------------------------------
// DirectorTimelineLanes — the stacked time lanes under the Director View
// players (DIR-004).
//
// Every lane shares one linear scale: delivered-file seconds across the full
// width. That is what makes the panel readable — a narration block sitting to
// the right of the scene cut above it IS the drift, visible without reading a
// single number.
//
// The playhead is deliberately NOT React state. It moves every frame, so it is
// written straight to a transform on a ref'd element from the same
// requestVideoFrameCallback loop the viewer uses, with a `timeupdate` backstop
// for when the tab is backgrounded and rVFC stops firing.
// ---------------------------------------------------------------------------

const LANE_HEIGHT = {
  ruler: 18,
  wave: 56,
  narration: 34,
  beats: 12,
  drift: 40,
} as const;

/** Roughly two pixels per drawn bucket keeps the envelope legible. */
const BUCKET_WIDTH_PX = 2;

function formatClock(seconds: number): string {
  const safe = Math.max(0, seconds);
  const minutes = Math.floor(safe / 60);
  const rest = Math.floor(safe % 60);
  return `${minutes}:${String(rest).padStart(2, "0")}`;
}

/** Tick spacing that yields a readable number of labels at any duration. */
function rulerStepSeconds(duration: number): number {
  for (const step of [5, 10, 15, 30, 60, 120]) {
    if (duration / step <= 12) return step;
  }
  return 300;
}

function useElementWidth(): [(node: HTMLDivElement | null) => void, number] {
  const [width, setWidth] = useState(0);
  const observerRef = useRef<ResizeObserver | null>(null);
  const ref = useCallback((node: HTMLDivElement | null) => {
    observerRef.current?.disconnect();
    if (!node) return;
    setWidth(node.clientWidth);
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setWidth(Math.round(entry.contentRect.width));
    });
    observer.observe(node);
    observerRef.current = observer;
  }, []);
  useEffect(() => () => observerRef.current?.disconnect(), []);
  return [ref, width];
}

/**
 * Drive a DOM node's horizontal position from the video clock without
 * re-rendering React on every frame.
 */
function usePlayhead(
  video: HTMLVideoElement | null,
  durationSeconds: number,
  width: number,
) {
  const playheadRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!video || durationSeconds <= 0 || width <= 0) return;

    const paint = (mediaTime?: number) => {
      const node = playheadRef.current;
      if (!node) return;
      const seconds = mediaTime ?? video.currentTime;
      if (!Number.isFinite(seconds)) return;
      const ratio = Math.min(1, Math.max(0, seconds / durationSeconds));
      node.style.transform = `translateX(${ratio * width}px)`;
    };

    const onTimeUpdate = () => paint();
    video.addEventListener("timeupdate", onTimeUpdate);
    video.addEventListener("seeked", onTimeUpdate);

    let handle: number | null = null;
    const supportsRvfc = "requestVideoFrameCallback" in video;
    if (supportsRvfc) {
      const onFrame = (
        _now: DOMHighResTimeStamp,
        metadata: VideoFrameCallbackMetadata,
      ) => {
        paint(metadata.mediaTime);
        handle = video.requestVideoFrameCallback(onFrame);
      };
      handle = video.requestVideoFrameCallback(onFrame);
    }
    paint();

    return () => {
      video.removeEventListener("timeupdate", onTimeUpdate);
      video.removeEventListener("seeked", onTimeUpdate);
      if (supportsRvfc && handle != null)
        video.cancelVideoFrameCallback(handle);
    };
  }, [video, durationSeconds, width]);

  return playheadRef;
}

function WaveformCanvas({
  peaks,
  width,
  height,
}: {
  peaks: WaveformPeaks;
  width: number;
  height: number;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || width <= 0) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.max(1, Math.floor(width * dpr));
    canvas.height = Math.max(1, Math.floor(height * dpr));
    const context = canvas.getContext("2d");
    if (!context) return;
    context.scale(dpr, dpr);
    context.clearRect(0, 0, width, height);

    const buckets = Math.max(1, Math.floor(width / BUCKET_WIDTH_PX));
    const drawn = resamplePeaks(peaks, buckets);
    // Normalise so a quietly mastered mix still fills the lane.
    const amplitude = Math.max(0.05, peakAmplitude(drawn));
    const middle = height / 2;
    // Inherit the theme's colour instead of hard-coding one for light/dark.
    context.fillStyle = window.getComputedStyle(canvas).color;

    for (let bucket = 0; bucket < drawn.buckets; bucket += 1) {
      const x = (bucket / drawn.buckets) * width;
      const top = middle - (drawn.max[bucket] / amplitude) * middle;
      const bottom = middle - (drawn.min[bucket] / amplitude) * middle;
      context.fillRect(
        x,
        top,
        BUCKET_WIDTH_PX - 0.5,
        Math.max(1, bottom - top),
      );
    }
  }, [peaks, width, height]);

  return (
    <canvas
      ref={canvasRef}
      data-testid="director-view-waveform"
      className="text-primary/70 block"
      style={{ width: `${width}px`, height: `${height}px` }}
    />
  );
}

export type DirectorTimelineLanesProps = {
  sync: SyncTimeline;
  narration: NarrationTimeline;
  drift: NarrationDriftReport;
  peaks: WaveformPeaks | null;
  /** Player the lanes follow and seek. */
  video: HTMLVideoElement | null;
  durationSeconds: number;
  onSeek: (seconds: number) => void;
  activeLineIndex: number;
  /** Beat grid + cut markers are engineering detail. */
  showBeats?: boolean;
  beatTimes?: number[];
  cutTimes?: number[];
};

export function DirectorTimelineLanes({
  sync,
  narration,
  drift,
  peaks,
  video,
  durationSeconds,
  onSeek,
  activeLineIndex,
  showBeats,
  beatTimes,
  cutTimes,
}: DirectorTimelineLanesProps) {
  const [containerRef, width] = useElementWidth();
  const playheadRef = usePlayhead(video, durationSeconds, width);

  const toX = useCallback(
    (seconds: number) =>
      durationSeconds > 0
        ? (Math.min(Math.max(seconds, 0), durationSeconds) / durationSeconds) *
          width
        : 0,
    [durationSeconds, width],
  );

  const driftByLine = useMemo(
    () => new Map(drift.lines.map((entry) => [entry.index, entry])),
    [drift.lines],
  );

  const handleSeek = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (durationSeconds <= 0) return;
      const bounds = event.currentTarget.getBoundingClientRect();
      if (bounds.width <= 0) return;
      const ratio = (event.clientX - bounds.left) / bounds.width;
      onSeek(Math.min(Math.max(ratio, 0), 1) * durationSeconds);
    },
    [durationSeconds, onSeek],
  );

  const ticks = useMemo(() => {
    if (durationSeconds <= 0) return [];
    const step = rulerStepSeconds(durationSeconds);
    const values: number[] = [];
    for (let second = 0; second <= durationSeconds; second += step) {
      values.push(second);
    }
    return values;
  }, [durationSeconds]);

  const driftPath = useMemo(() => {
    if (drift.curve.length === 0 || width <= 0) return null;
    const magnitude = Math.max(
      0.5,
      ...drift.curve.map((point) => Math.abs(point.shiftSeconds)),
    );
    const middle = LANE_HEIGHT.drift / 2;
    // A step curve, not a smooth line: the shift is constant within a scene and
    // jumps at the cut, which is exactly how the error accumulates.
    const segments = drift.curve.map((point, index) => {
      const x = toX(point.startSeconds);
      const y = middle - (point.shiftSeconds / magnitude) * (middle - 4);
      return `${index === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}${
        index < drift.curve.length - 1
          ? `L${toX(drift.curve[index + 1].startSeconds).toFixed(1)},${y.toFixed(1)}`
          : `L${width.toFixed(1)},${y.toFixed(1)}`
      }`;
    });
    return { path: segments.join(""), magnitude };
  }, [drift.curve, toX, width]);

  const contentEnd = sync.contentEndSeconds;

  return (
    <div ref={containerRef} className="relative w-full select-none">
      {/* Ruler */}
      <div
        className="text-muted-foreground relative text-[10px]"
        style={{ height: LANE_HEIGHT.ruler }}
      >
        {width > 0 &&
          ticks.map((second) => (
            <span
              key={second}
              className="absolute top-0 -translate-x-1/2 tabular-nums"
              style={{ left: toX(second) }}
            >
              {formatClock(second)}
            </span>
          ))}
      </div>

      {/* Clickable lane stack */}
      <div
        className="relative cursor-pointer"
        onClick={handleSeek}
        role="presentation"
        data-testid="director-view-lanes"
      >
        {/* Delivered mix waveform */}
        <div
          className="bg-muted/40 relative overflow-hidden rounded"
          style={{ height: LANE_HEIGHT.wave }}
        >
          {peaks && width > 0 ? (
            <WaveformCanvas
              peaks={peaks}
              width={width}
              height={LANE_HEIGHT.wave}
            />
          ) : (
            <div className="text-muted-foreground flex h-full items-center justify-center text-[11px]">
              waveform unavailable
            </div>
          )}
          {/* Logo outro: past this point there is no more story. */}
          {contentEnd != null && durationSeconds > 0 && (
            <div
              className="bg-foreground/5 border-foreground/20 absolute inset-y-0 border-l"
              style={{
                left: toX(contentEnd),
                width: Math.max(0, width - toX(contentEnd)),
              }}
              title="logo outro"
            />
          )}
        </div>

        {/* Narration blocks */}
        <div
          className="bg-muted/20 relative mt-1 rounded"
          style={{ height: LANE_HEIGHT.narration }}
        >
          {width > 0 &&
            narration.lines.map((line) => {
              const left = toX(line.startSeconds);
              const right = toX(line.endSeconds);
              const lineDrift = driftByLine.get(line.index);
              const severity = lineDrift?.severity ?? "ok";
              return (
                <div
                  key={line.index}
                  data-testid={`director-view-narration-block-${line.index}`}
                  className={cn(
                    "absolute top-1 bottom-1 rounded-sm border text-[10px] leading-none",
                    severity === "critical" &&
                      "border-destructive/60 bg-destructive/25",
                    severity === "warn" &&
                      "border-amber-500/60 bg-amber-500/25",
                    severity === "ok" && "border-primary/40 bg-primary/20",
                    line.index === activeLineIndex &&
                      "ring-primary ring-2 ring-offset-0",
                  )}
                  style={{
                    left,
                    width: Math.max(2, right - left),
                  }}
                  title={`${formatClock(line.startSeconds)} — ${line.text}`}
                />
              );
            })}
        </div>

        {/* Beat grid (engineering detail) */}
        {showBeats && (
          <div
            className="relative mt-1"
            style={{ height: LANE_HEIGHT.beats }}
            data-testid="director-view-beats"
          >
            {width > 0 &&
              (beatTimes ?? []).map((beat, index) => (
                <div
                  key={`beat-${index}`}
                  className="bg-muted-foreground/30 absolute top-1 bottom-1 w-px"
                  style={{ left: toX(beat + sync.posterOffsetSeconds) }}
                />
              ))}
            {width > 0 &&
              (cutTimes ?? []).map((cut, index) => (
                <div
                  key={`cut-${index}`}
                  className="bg-primary/70 absolute inset-y-0 w-px"
                  style={{ left: toX(cut + sync.posterOffsetSeconds) }}
                  title="beat-aligned cut"
                />
              ))}
          </div>
        )}

        {/* Drift curve */}
        {driftPath && (
          <div
            className="relative mt-1"
            style={{ height: LANE_HEIGHT.drift }}
            data-testid="director-view-drift-curve"
          >
            <svg
              width={width}
              height={LANE_HEIGHT.drift}
              className="block overflow-visible"
              aria-hidden="true"
            >
              <line
                x1={0}
                y1={LANE_HEIGHT.drift / 2}
                x2={width}
                y2={LANE_HEIGHT.drift / 2}
                className="stroke-muted-foreground/30"
                strokeDasharray="3 3"
                strokeWidth={1}
              />
              <path
                d={driftPath.path}
                fill="none"
                className={cn(
                  "stroke-2",
                  drift.summary.rampDetected
                    ? "stroke-destructive"
                    : "stroke-primary/70",
                )}
              />
            </svg>
            <span className="text-muted-foreground absolute top-0 right-0 text-[10px]">
              ±{driftPath.magnitude.toFixed(1)}s scene shift
            </span>
          </div>
        )}

        {/* Playhead across every lane */}
        <div
          ref={playheadRef}
          className="bg-foreground pointer-events-none absolute top-0 bottom-0 left-0 w-px"
          data-testid="director-view-playhead"
        />
      </div>
    </div>
  );
}
